import React, { useEffect, useState } from "react";
import Main from "../../components/layout/Main";
import { Col, Row, Card, Tabs, Select, Table, Badge, Input, Button, Drawer, Switch, Tooltip } from "antd";
import {
  TeamOutlined,
  SolutionOutlined,
  PictureOutlined,
  SettingOutlined,
  UpOutlined,
  DownOutlined,
  EyeInvisibleOutlined,
  HolderOutlined,
} from "@ant-design/icons";
import useRequest from "../../hooks/useRequest";
import moment from "moment";
import { Severty, ShowToast } from "../../helper/toast";
import { Line } from "react-chartjs-2";
import { Link } from "react-router-dom";
import LastProducts from "./_LastProducts";
import SectionWrapper from "../../components/SectionWrapper";
import lang from "../../helper/lang";
import LastAppointments from "./_LastAppointments";
import LastTransaction from "./_LastTransaction";
import YearlyChartSection from "./_YearlyChartSection";
import MonthlyChartSection from "./_MonthlyChartSection";
import WeeklyChartSection from "./_WeeklyChartSection";
import apiPath from "../../constants/apiPath";
import  ExpiredSubscription from "./_ExpiredSubscription"; 
import { globalYears } from "../../helper/Var";
import UserImage from "../../assets/images/user.png";
import ActiveCheckIcon from "../../assets/images/active-check-icon.svg";
import FileIcon from "../../assets/images/file-icon.svg";

const { TabPane } = Tabs;

// The dashboard's draggable/collapsible/removable card sections, in their
// DEFAULT order. Adding a new section to the dashboard later just means
// adding an entry here (and a renderSection(...) call below) — any admin's
// already-saved preference automatically appends it rather than losing it.
const DEFAULT_DASHBOARD_SECTIONS = [
  { id: "latestPosts", title: "Latest Published Posts" },
  { id: "latestCommunications", title: "Latest Communications" },
  { id: "upcomingRenewals", title: "Upcoming Renewals" },
  { id: "upcomingPaymentRenewals", title: "Upcoming Payment Renewals" },
  { id: "expiredContracts", title: "Expired Contracts (Not Renewed)" },
];
const DEFAULT_SECTION_ORDER = DEFAULT_DASHBOARD_SECTIONS.map((s) => s.id);

const Home = () => {
  const { request } = useRequest();
  const [dashboard, setDashboard] = useState({});
  const [loading, setLoading] = useState(false);
  const [years, setYears] = useState();
  const [posts, setPosts] = useState([]);
  const [feedbackData, setFeedbackData] = useState([]);
  const [upcomingRenewals, setUpcomingRenewals] = useState([]);
  const [expiredContracts, setExpiredContracts] = useState([]);
  const [upcomingPaymentRenewals, setUpcomingPaymentRenewals] = useState([]);
  const [upcomingPagination, setUpcomingPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [expiredPagination, setExpiredPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [paymentRenewalPagination, setPaymentRenewalPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [upcomingSearch, setUpcomingSearch] = useState("");
  const [expiredSearch, setExpiredSearch] = useState("");
  const [paymentRenewalSearch, setPaymentRenewalSearch] = useState("");

  // ── Customizable dashboard: drag-to-reorder, minimize, and hide/show
  // sections. Persisted per-admin in localStorage — not shared across
  // devices, but survives reloads/logouts and doesn't need a backend field
  // just to remember "this card is collapsed."
  const dashboardPrefsKey = (() => {
    try {
      const admin = JSON.parse(localStorage.getItem("admin_userProfile") || "null");
      return `admin_dashboard_prefs_${admin?._id || "default"}`;
    } catch {
      return "admin_dashboard_prefs_default";
    }
  })();
  const [sectionOrder, setSectionOrder] = useState(DEFAULT_SECTION_ORDER);
  const [hiddenSections, setHiddenSections] = useState(new Set());
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [draggedSectionId, setDraggedSectionId] = useState(null);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(dashboardPrefsKey) || "null");
      if (saved) {
        // Merge in any section added to DEFAULT_DASHBOARD_SECTIONS after this
        // admin last saved their layout, so a newly-added card (like this
        // one) still shows up instead of silently disappearing for them.
        const savedOrder = (saved.order || []).filter((id) => DEFAULT_SECTION_ORDER.includes(id));
        const missing = DEFAULT_SECTION_ORDER.filter((id) => !savedOrder.includes(id));
        setSectionOrder([...savedOrder, ...missing]);
        setHiddenSections(new Set(saved.hidden || []));
        setCollapsedSections(new Set(saved.collapsed || []));
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistDashboardPrefs = (order, hidden, collapsed) => {
    try {
      localStorage.setItem(
        dashboardPrefsKey,
        JSON.stringify({ order, hidden: Array.from(hidden), collapsed: Array.from(collapsed) })
      );
    } catch {}
  };

  const toggleSectionCollapsed = (id) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      persistDashboardPrefs(sectionOrder, hiddenSections, next);
      return next;
    });
  };

  const hideSection = (id) => {
    setHiddenSections((prev) => {
      const next = new Set(prev);
      next.add(id);
      persistDashboardPrefs(sectionOrder, next, collapsedSections);
      return next;
    });
  };

  const toggleSectionVisible = (id, visible) => {
    setHiddenSections((prev) => {
      const next = new Set(prev);
      visible ? next.delete(id) : next.add(id);
      persistDashboardPrefs(sectionOrder, next, collapsedSections);
      return next;
    });
  };

  const handleSectionDrop = (targetId) => {
    if (!draggedSectionId || draggedSectionId === targetId) {
      setDraggedSectionId(null);
      return;
    }
    setSectionOrder((prev) => {
      const next = [...prev];
      const fromIdx = next.indexOf(draggedSectionId);
      const toIdx = next.indexOf(targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, draggedSectionId);
      persistDashboardPrefs(next, hiddenSections, collapsedSections);
      return next;
    });
    setDraggedSectionId(null);
  };

  const resetDashboardLayout = () => {
    setSectionOrder(DEFAULT_SECTION_ORDER);
    setHiddenSections(new Set());
    setCollapsedSections(new Set());
    persistDashboardPrefs(DEFAULT_SECTION_ORDER, new Set(), new Set());
  };

  // Wraps one dashboard card with the drag handle, minimize toggle, and
  // hide button — every section below is rendered through this so the
  // customization chrome stays identical across all of them. Returns null
  // for a hidden section (still occupies its slot in sectionOrder, just
  // not rendered).
  const renderSection = (id, title, extraNode, bodyNode) => {
    if (hiddenSections.has(id)) return null;
    const isCollapsed = collapsedSections.has(id);
    return (
      <Row
        key={id}
        className={`rowgap-vbox mt-3 dashboard-section ${draggedSectionId === id ? "dashboard-section-dragging" : ""}`}
        gutter={[16, 16]}
        onDragOver={(e) => e.preventDefault()}
        onDrop={() => handleSectionDrop(id)}
      >
        <Col span={24}>
          <Card
            title={
              <span
                className="dashboard-section-drag-handle"
                draggable
                onDragStart={() => setDraggedSectionId(id)}
                onDragEnd={() => setDraggedSectionId(null)}
              >
                <HolderOutlined />
                {title}
              </span>
            }
            bordered={false}
            className="criclebox tablespace home-header-nowrap"
            extra={
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {extraNode}
                <Tooltip title={isCollapsed ? "Expand" : "Minimize"}>
                  <Button
                    size="small"
                    type="text"
                    icon={isCollapsed ? <DownOutlined /> : <UpOutlined />}
                    onClick={() => toggleSectionCollapsed(id)}
                  />
                </Tooltip>
                <Tooltip title="Remove from dashboard">
                  <Button size="small" type="text" icon={<EyeInvisibleOutlined />} onClick={() => hideSection(id)} />
                </Tooltip>
              </div>
            }
          >
            {!isCollapsed && bodyNode}
          </Card>
        </Col>
      </Row>
    );
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
    fetchUpcomingRenewals();
    fetchExpiredContracts();
    fetchUpcomingPaymentRenewals();
  }, []);

  useEffect(() => {
    fetchUpcomingRenewals();
  }, [upcomingPagination.current, upcomingPagination.pageSize, upcomingSearch]);

  useEffect(() => {
    fetchExpiredContracts();
  }, [expiredPagination.current, expiredPagination.pageSize, expiredSearch]);

  useEffect(() => {
    fetchUpcomingPaymentRenewals();
  }, [paymentRenewalPagination.current, paymentRenewalPagination.pageSize, paymentRenewalSearch]);

  const columnsCommunications = [
    {
      title: "Client Name",
      dataIndex: "name",
      key: "name",
      render: (name , record) => (
        <div className="color-badge-table">
          <img width={30} src={record.image || UserImage } alt="icon" />
          <div className="tag-name-text">
            <p>{name}</p>
          </div>
        </div>
      ),
    },

    {
      title: "Feedback",
      dataIndex: "comment",
      key: "comment",
      render: (comment) => (
        <div className="color-badge-table">
          <p>{comment?.length > 45 ? `${comment.slice(0, 45)}...` : comment}</p>
        </div>
      ),
    },
    {
      title: "Content Type",
      dataIndex: "source",
      key: "source",
      render: (source) => (
        <div className="color-badge-table">
          <img src={FileIcon} alt="icon" />
          {source}
        </div>
      ),
    },
    {
      title: "Upload Date",
      dataIndex: "time",
      key: "time",
      render: (time) =>
        new Date(time).toLocaleString("en-US", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    },
  ];

  const columnsPosts = [
    {
      title: "Creator",
      dataIndex: "userName",
      key: "userName",
      render: (userName, record) => (
        <div className="color-badge-table">
          <img width={30} src={record.userImage || UserImage} alt="icon" />
          <div className="tag-name-text">
            <p>{userName}</p>
          </div>
        </div>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title) => (
        <div className="color-badge-table">
          <p>{title?.length > 45 ? `${title.slice(0, 45)}...` : title}</p>
        </div>
      ),
    },
    {
      title: "Post Type",
      dataIndex: "postType",
      key: "postType",
      render: (postType) => (
        <div className="color-badge-table">
          <img src={FileIcon} alt="icon" />
          {postType}
        </div>
      ),
    },
    {
      title: "Post Text",
      dataIndex: "postText",
      key: "postText",
      render: (postText) => (
        <div className="color-badge-table">
          <p>{postText?.length > 45 ? `${postText.slice(0, 45)}...` : postText}</p>
        </div>
      ),
    },

    {
      title: "Published Date",
      dataIndex: "selectedDate",
      key: "selectedDate",
      render: (time) =>
        new Date(time).toLocaleString("en-US", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    },
  ];
  

  const count = [
    {
      title: lang("Total Customers"),
      value: dashboard?.customerCount || 0,
      icon: <TeamOutlined />,
      cls: "card-0",
      url: "/customer-manager",
    },
    {
      title: lang("Total Brand Managers And Strategist"),
      value: dashboard?.brandManagerCount || 0,
      icon: <SolutionOutlined />,
      cls: "card-1",
      url: "/brand-manager",
    },
    {
      title: lang("Total Graphics Team Members"),
      value: dashboard?.graphicsMemberCount || 0,
      icon: <PictureOutlined />,
      cls: "card-4",
      url: "/workload",
    },
    // {
    //   title: lang("Total Vendors"),
    //   value: dashboard?.vendorCount || 1,
    //   icon: VendorImg,
    //   url: "/vendor-manager",
    // },
    // {
    //   title: lang("Total Designer"),
    //   value: dashboard?.designerCount || 0,
    //   icon: VendorImg,
    //   url: "/designer-manager",
    // },
    // {
    //   title: lang("Total Service Categories"),
    //   value: dashboard?.srcCategoryCount || 0,
    //   icon: CatgoriesImg,
    //   url: "/service-category",
    // },
    // {
    //   title: lang("Designer Service"),
    //   value: dashboard?.srcCatalogueCount || 0,
    //   icon: Servisecatalogue1Img,
    //   url: "/designer-service",
    // },
    // {
    //   title: lang("Vendor Orders"),
    //   value: dashboard?.orderCount || 0,
    //   icon: TotalOrderdImg,
    //   url: "/vendor-order",
    // },
    // {
    //   title: lang("Designer Orders"),
    //   value: dashboard?.orderCount || 0,
    //   icon: TotalOrderdImg,
    //   url: "/designer-order",
    // },
    // {
    //   title: lang("Designer Bookings"),
    //   value: dashboard?.bookingCount || 0,
    //   icon: TotalOrderdImg,
    //   url: "/designer-booking",
    // },
    // {
    //   title: lang("New Member waiting Approval"),
    //   value: dashboard?.waitingApprovalCount || 0,
    //   icon: CataloguesImg,
    //   url: "/vendor-manager",
    // },
  ];

  const fetchData = () => {
    request({
      url: apiPath.dashboard,
      method: "GET",
      onSuccess: ({ data }) => {
        setLoading(false);
        console.log(data);
        setDashboard(data);
        // Map Activity Feed posts
        const mappedPosts = Array.isArray(data?.last10PublishedPosts)
          ? data.last10PublishedPosts.map((post) => ({
              key: post?._id,
              userName: post?.userId?.name || "",
              userImage: post?.userId?.image || "",
              postText: post?.postText || "",
              title: post?.title || "",
              postType: post?.postType || "",
              selectedDate: post?.selectedDate || "",
              status: [post?.brandStatus, post?.clientStatus].filter(Boolean).join(" • "),
              created_at: post?.created_at,
            }))
          : [];
        setPosts(mappedPosts);

        // Map Client Feedback table data
        const mappedFeedback = Array.isArray(data?.latest10Comments)
          ? data.latest10Comments.map((item) => ({
              key: `${item?.postId || ""}-${item?.time || Math.random()}`,
              name: item?.commentUser?.name || "",
              image: item?.commentUser?.image || "",
              comment: item?.comment || "",
              source: item?.title || "",
              time: item?.time,
            }))
          : [];
        setFeedbackData(mappedFeedback);
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const fetchUpcomingRenewals = () => {
    const { current, pageSize } = upcomingPagination;
    request({
      url: `${apiPath.upcomingRenewals}?page=${current}&limit=${pageSize}&search=${upcomingSearch}`,
      method: "GET",
      onSuccess: (data) => {
        console.log("Upcoming renewals:", data);
        setUpcomingRenewals(data?.data?.renewals || []);
        setUpcomingPagination((prev) => ({
          ...prev,
          total: data?.data?.total || 0,
        }));
      },
      onError: (error) => {
        console.error("Error fetching upcoming renewals:", error);
        ShowToast(error?.response?.data?.message || "Failed to fetch upcoming renewals", Severty.ERROR);
        setUpcomingRenewals([]);
      },
    });
  };

  const fetchExpiredContracts = () => {
    const { current, pageSize } = expiredPagination;
    request({
      url: `${apiPath.expiredContracts}?page=${current}&limit=${pageSize}&search=${expiredSearch}`,
      method: "GET",
      onSuccess: (data) => {
        console.log("Expired contracts:", data);
        setExpiredContracts(data?.data?.expired || []);
        setExpiredPagination((prev) => ({
          ...prev,
          total: data?.data?.total || 0,
        }));
      },
      onError: (error) => {
        console.error("Error fetching expired contracts:", error);
        ShowToast(error?.response?.data?.message || "Failed to fetch expired contracts", Severty.ERROR);
        setExpiredContracts([]);
      },
    });
  };

  const fetchUpcomingPaymentRenewals = () => {
    const { current, pageSize } = paymentRenewalPagination;
    request({
      url: `${apiPath.upcomingPaymentRenewals}?page=${current}&limit=${pageSize}&search=${paymentRenewalSearch}`,
      method: "GET",
      onSuccess: (data) => {
        setUpcomingPaymentRenewals(data?.data?.renewals || []);
        setPaymentRenewalPagination((prev) => ({
          ...prev,
          total: data?.data?.total || 0,
        }));
      },
      onError: (error) => {
        console.error("Error fetching upcoming payment renewals:", error);
        ShowToast(error?.response?.data?.message || "Failed to fetch upcoming payment renewals", Severty.ERROR);
        setUpcomingPaymentRenewals([]);
      },
    });
  };

  const upcomingRenewalsColumns = [
    {
      title: "Client Name",
      dataIndex: "clientName",
      key: "clientName",
      render: (name) => (
        <div className="color-badge-table">
          <p>{name}</p>
        </div>
      ),
    },
    {
      title: "Renewal Date",
      dataIndex: "renewalDate",
      key: "renewalDate",
      render: (date) => moment(date).format("DD-MM-YYYY"),
    },
    {
      title: "Contract Type",
      dataIndex: "renewalNumber",
      key: "renewalNumber",
      render: (num) => (
        <Badge
          color={num === 0 ? "blue" : "green"}
          text={num === 0 ? "Original Contract" : `Renewal ${num}`}
        />
      ),
    },
    {
      title: "Contract Length",
      dataIndex: "contractLength",
      key: "contractLength",
      render: (length) => (length ? `${length} month(s)` : "N/A"),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => (amount ? `$${parseFloat(amount).toFixed(2)}` : "N/A"),
    },
  ];

  const expiredContractsColumns = [
    {
      title: "Client Name",
      dataIndex: "clientName",
      key: "clientName",
      render: (name) => (
        <div className="color-badge-table">
          <p>{name}</p>
        </div>
      ),
    },
    {
      title: "Expired Date",
      dataIndex: "expiredDate",
      key: "expiredDate",
      render: (date) => moment(date).format("DD-MM-YYYY"),
    },
    {
      title: "Contract Type",
      dataIndex: "renewalNumber",
      key: "renewalNumber",
      render: (num) => (
        <Badge
          color={num === 0 ? "blue" : "green"}
          text={num === 0 ? "Original Contract" : `Renewal ${num}`}
        />
      ),
    },
    {
      title: "Contract Length",
      dataIndex: "contractLength",
      key: "contractLength",
      render: (length) => (length ? `${length} month(s)` : "N/A"),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => (amount ? `$${parseFloat(amount).toFixed(2)}` : "N/A"),
    },
  ];

  // Payment-renewal tracker — distinct from Upcoming Renewals/Expired
  // Contracts above (those are about the contract's own term/endDate); this
  // is about the recurring paid window (paidUntil) that "Renew Payment"
  // extends each billing cycle. Status flips from "due_soon" to "expired"
  // automatically once paymentDueDate passes with nothing further paid.
  const paymentRenewalsColumns = [
    {
      title: "Client Name",
      dataIndex: "clientName",
      key: "clientName",
      render: (name) => (
        <div className="color-badge-table">
          <p>{name}</p>
        </div>
      ),
    },
    {
      title: "Payment Due Date",
      dataIndex: "paymentDueDate",
      key: "paymentDueDate",
      render: (date) => moment(date).format("DD-MM-YYYY"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <Badge
          color={status === "expired" ? "red" : "green"}
          text={
            status === "expired"
              ? `Expired ${Math.abs(record.daysRemaining)} day(s) ago`
              : `Due in ${record.daysRemaining} day(s)`
          }
        />
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount, record) => (amount ? `${record.currency || "$"} ${parseFloat(amount).toFixed(2)}` : "N/A"),
    },
  ];

  return (
    <>
      <div className="home-overview-strip">
        <div>
          <div className="home-overview-eyebrow">Platform Overview</div>
          <div className="home-overview-date">{moment().format("dddd, MMMM D, YYYY")}</div>
        </div>
        <Button icon={<SettingOutlined />} onClick={() => setCustomizeOpen(true)}>
          Customize Dashboard
        </Button>
      </div>

      <Row className="rowgap-vbox" gutter={[24, 16]}>
        {count.map((c, index) => {
          return (
            <Col
              key={index}
              xs={24}
              sm={24}
              md={12}
              lg={12}
              xl={8}
              xxl={6}
              className="mb24"
              style={{ paddingRight: "0px" }}
            >
              <Link to={c.url}>
                <Card
                  bordered={false}
                  className={`criclebox count-card ${c.cls}`}
                  style={{ height: "100%" }}
                >
                  <div className="number-dash-count">
                    <div className="number">
                      <span>{c?.title}</span>
                      <h3>{c?.value}</h3>
                    </div>
                    <div className="icon-box">{c?.icon}</div>
                  </div>
                </Card>
              </Link>
            </Col>
          );
        })}
      </Row>
      {(() => {
        const sectionBodies = {
          latestPosts: () => renderSection(
            "latestPosts",
            "Latest Published Posts",
            null,
            <div className="table-responsive">
              <Table columns={columnsPosts} dataSource={posts} pagination={false} className="table-responsive" />
            </div>
          ),
          latestCommunications: () => renderSection(
            "latestCommunications",
            "Latest Communications",
            null,
            <div className="table-responsive">
              <Table columns={columnsCommunications} dataSource={feedbackData} pagination={false} className="table-responsive" />
            </div>
          ),
          upcomingRenewals: () => renderSection(
            "upcomingRenewals",
            "Upcoming Renewals",
            <Input.Search
              placeholder="Search by client name"
              allowClear
              style={{ width: 250 }}
              onSearch={(value) => {
                setUpcomingSearch(value);
                setUpcomingPagination((prev) => ({ ...prev, current: 1 }));
              }}
              onChange={(e) => {
                if (!e.target.value) {
                  setUpcomingSearch("");
                  setUpcomingPagination((prev) => ({ ...prev, current: 1 }));
                }
              }}
            />,
            <div className="table-responsive">
              <Table
                columns={upcomingRenewalsColumns}
                dataSource={upcomingRenewals}
                rowKey="_id"
                pagination={{
                  current: upcomingPagination.current,
                  pageSize: upcomingPagination.pageSize,
                  total: upcomingPagination.total,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} renewals`,
                  onChange: (page, pageSize) => {
                    setUpcomingPagination((prev) => ({ ...prev, current: page, pageSize }));
                  },
                }}
                className="table-responsive"
              />
            </div>
          ),
          upcomingPaymentRenewals: () => renderSection(
            "upcomingPaymentRenewals",
            "Upcoming Payment Renewals",
            <Input.Search
              placeholder="Search by client name"
              allowClear
              style={{ width: 250 }}
              onSearch={(value) => {
                setPaymentRenewalSearch(value);
                setPaymentRenewalPagination((prev) => ({ ...prev, current: 1 }));
              }}
              onChange={(e) => {
                if (!e.target.value) {
                  setPaymentRenewalSearch("");
                  setPaymentRenewalPagination((prev) => ({ ...prev, current: 1 }));
                }
              }}
            />,
            <div className="table-responsive">
              <Table
                columns={paymentRenewalsColumns}
                dataSource={upcomingPaymentRenewals}
                rowKey="clientId"
                pagination={{
                  current: paymentRenewalPagination.current,
                  pageSize: paymentRenewalPagination.pageSize,
                  total: paymentRenewalPagination.total,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} payment renewals`,
                  onChange: (page, pageSize) => {
                    setPaymentRenewalPagination((prev) => ({ ...prev, current: page, pageSize }));
                  },
                }}
                className="table-responsive"
              />
            </div>
          ),
          expiredContracts: () => renderSection(
            "expiredContracts",
            "Expired Contracts (Not Renewed)",
            <Input.Search
              placeholder="Search by client name"
              allowClear
              style={{ width: 250 }}
              onSearch={(value) => {
                setExpiredSearch(value);
                setExpiredPagination((prev) => ({ ...prev, current: 1 }));
              }}
              onChange={(e) => {
                if (!e.target.value) {
                  setExpiredSearch("");
                  setExpiredPagination((prev) => ({ ...prev, current: 1 }));
                }
              }}
            />,
            <div className="table-responsive">
              <Table
                columns={expiredContractsColumns}
                dataSource={expiredContracts}
                rowKey="_id"
                pagination={{
                  current: expiredPagination.current,
                  pageSize: expiredPagination.pageSize,
                  total: expiredPagination.total,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} expired contracts`,
                  onChange: (page, pageSize) => {
                    setExpiredPagination((prev) => ({ ...prev, current: page, pageSize }));
                  },
                }}
                className="table-responsive"
              />
            </div>
          ),
        };
        return sectionOrder.map((id) => sectionBodies[id]?.());
      })()}

      {/* Customize Dashboard drawer — the only way to bring back a section
          hidden via its card's eye-off button, and a full reset. Reordering
          and minimizing happen directly on the cards themselves. */}
      <Drawer
        title="Customize Dashboard"
        placement="right"
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        width={360}
      >
        <p style={{ color: "#8c8c8c", fontSize: 13, marginBottom: 20 }}>
          Drag a card by its title to reorder it, use the arrow to minimize
          it, or the eye icon to remove it from your dashboard. Bring a
          removed section back here.
        </p>
        {sectionOrder.map((id) => {
          const def = DEFAULT_DASHBOARD_SECTIONS.find((s) => s.id === id);
          if (!def) return null;
          return (
            <div
              key={id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 0",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <span>{def.title}</span>
              <Switch
                checked={!hiddenSections.has(id)}
                onChange={(checked) => toggleSectionVisible(id, checked)}
              />
            </div>
          );
        })}
        <Button block style={{ marginTop: 24 }} onClick={resetDashboardLayout}>
          Reset to Default Layout
        </Button>
      </Drawer>
    </>
  );
};

export default Home;
